<template>
    <div class="longterm-plots__download">
        <button class="link" @click="downloadTxt()">
            <i class="icon-download"></i><span>{{ $t.txt }}</span>
        </button>
        <button class="link" @click="downloadPdf()">
            <i class="icon-download"></i><span>{{ $t.pdf }}</span>
        </button>
        <img src="/static/images/ESA_Logo_black.svg"  class="logo-img pdf esa" />
    </div>
</template>

<script>
import LongtermService from '@/services/longterm.service'
import ObjectsService from '@/services/objects.service'
import Config from '@/constants/config'
import UtilsService from '@/services/utils.service'
import html2canvas from 'html2canvas'
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import TimeFrameService from '@/services/timeframe.service'
import ObservatoryService from '@/services/observatory.service'
import SpinnerService from '@/services/spinner.service'

export default {
    name: 'LongTermDownload',
    props: {
        object: Object
    },
    data() {
        return {
            fileContent: '',
            fileName: Config.longtermPlots.downloadFileName,
            docDefinition: {
                content: [],
                footer: {
                    margin: 15,
                    fontSize: 9,
                    alignment: 'right',
                    text: (new Date()).getFullYear() + ' ' + Config.attributionText,
                },
            },
            visualiseOnChartFlag: false,
        }
    },
    computed: {
        longtermObjectName() {
            return LongtermService.getLongtermObjectName();
        },
        visualiseOnChart() {
            return LongtermService.getLongtermVisualiseOnChart();
        }
    },
    methods: {
        prepareTxtDownload() {
            const objectName = ObjectsService.getDesignatorAndName(this.longtermObjectName);
            const head1 = [this.$t.object, this.$t.date];
            const head2 = [this.$t.designatorName, this.$t.utcTimeSymbol];

            for (const plot in this.object.plots) {
                if (this.object.plots[plot].enable) {
                    head1.push(`${this.$keys.titles[plot]} (${this.object.plots[plot].units})`);
                    head2.push(`${plot} (${this.object.plots[plot].units})`);
                }
            }

            this.fileContent = '';
            this.fileContent += head1.join(',') + '\n';
            this.fileContent += head2.join(',') + '\n';

            const timestampsReduced = UtilsService.reduceDensity(this.object.timestamps);
            for (const [index, julianDate] of timestampsReduced.entries()) {
                const timestamp = UtilsService.julianToTimestamp(julianDate);
                const date = UtilsService.momentUtcDate(timestamp, true);
                const row = [];
                row.push(`"${objectName}"`);
                row.push(date); 
                for (const plot in this.object.plots) {
                    if (this.object.plots[plot].enable) {
                        const reducedValues = UtilsService.reduceDensity(this.object.plots[plot].values);
                        const value = this.rounding(plot, reducedValues[index]);
                        row.push(value);
                    }
                }
                this.fileContent += row.join(',') + '\n';
            }
        },

        downloadTxt() {
            this.prepareTxtDownload();
            UtilsService.downloadAsciiFile(this.fileContent, this.fileName);
        },

        addInformationLine(label, text) {
            this.docDefinition.content.push(
                {
                    margin: [0, 5],
                    text: [
                        {
                            text: label+': ',
                            style: { bold: true }
                        },
                        text,
                    ]
                }
            );
        },

        addImage(image) {
            this.docDefinition.content.push({    
                margin: [0, 0, 0, 50],
                alignment: 'center',
                image,
                fit: [500,500],
                pageBreak: 'after'
            })
        },

        addChartHeader(text) {
            this.docDefinition.content.push(
                {
                    text,
                    margin: [0, 50, 0, 20],
                    alignment: 'center',
                    style: { bold: true }
                }
            );
        },

        addLogos() {
            this.docDefinition.content.push(
                {   
                    margin: [0, 0, 0, 50],
                    columns: [
                        {
                            alignment: 'right',
                            svg: '<svg xmlns="http://www.w3.org/2000/svg" width="88.928" height="32" viewBox="0 0 88.928 32"><path d="M25.889,21.284" transform="translate(1.726 1.419)" fill="#aaa"/><g transform="translate(36.458 7.2)"><path d="M60.479,14.031c-1.373-.254-2.949-.305-3.762-1.219a1.512,1.512,0,0,1-.154-1.525,3.191,3.191,0,0,1,3.254-1.374,2.9,2.9,0,0,1,2.694,1.627,3.846,3.846,0,0,1,.305,1.323H66.58A5.7,5.7,0,0,0,65.1,8.9a8.7,8.7,0,0,0-7.318-1.983c-1.831.254-4.271,1.321-5.086,3.712a4.79,4.79,0,0,0,1.373,5.337c1.729,1.323,4.982,1.679,6.305,1.985,1.524.3,2.288.508,2.541,1.371A1.948,1.948,0,0,1,62.666,21c-.967,1.27-2.949,1.373-4.526.915-1.322-.405-1.882-1.475-2.135-2.9H52.041a5.823,5.823,0,0,0,1.067,3.306,6.458,6.458,0,0,0,4.626,2.847,10.642,10.642,0,0,0,6.406-.714,7.447,7.447,0,0,0,2.084-1.881,5.555,5.555,0,0,0,.253-5.794C65.359,14.845,62.818,14.438,60.479,14.031Z" transform="translate(-32.989 -6.747)" fill="#aaa"/><path d="M48.552,9.306a8.151,8.151,0,0,0-7.319-2.442,7.953,7.953,0,0,0-5.643,3.865,11.443,11.443,0,0,0-1.323,3.763c-.407,3.1.458,8.286,5.9,10.32a8.849,8.849,0,0,0,8.185-1.169,8.051,8.051,0,0,0,2.847-4.73H46.672a3.587,3.587,0,0,1-1.322,2.034,4.181,4.181,0,0,1-4.83-.2c-1.373-1.017-1.829-2.693-1.881-5.084,0-1.27.356-3.3,1.423-4.27a4.2,4.2,0,0,1,3.967-.814,3.71,3.71,0,0,1,2.491,3.558H40.113v3.2H51.2a12.111,12.111,0,0,0-.305-3.356A11.886,11.886,0,0,0,48.552,9.306Z" transform="translate(-34.18 -6.749)" fill="#aaa"/><path d="M83.837,20.426V14.935a22.451,22.451,0,0,0-.355-4.169c-.305-1.322-1.475-3.508-5.237-3.966-.7-.05-1.45-.05-2.2-.05h-.034a11.965,11.965,0,0,0-3.626.659A5.612,5.612,0,0,0,69.027,12.8h4.066a2.642,2.642,0,0,1,1.577-2.541A3.651,3.651,0,0,1,76.042,10v0a3.714,3.714,0,0,1,2.71.867,1.778,1.778,0,0,1,.46,1.728c-.255,1.068-1.424,1.423-3.2,1.678v.005c-.6.1-1.3.149-2.047.249-2.135.254-5.186,1.322-5.9,4.423a5.1,5.1,0,0,0,1.829,4.93,7.328,7.328,0,0,0,4.423,1.374,9.161,9.161,0,0,0,1.73-.153v-.009a8.437,8.437,0,0,0,3.572-1.822,5.624,5.624,0,0,0,.508,1.475H84.4A10.243,10.243,0,0,1,83.837,20.426Zm-4.576-.66a3.639,3.639,0,0,1-3.253,2.083v.006a3.222,3.222,0,0,1-.98.044,2.409,2.409,0,0,1-2.237-1.27,2.058,2.058,0,0,1,.1-1.983c.61-1.067,1.678-1.27,3.153-1.575v-.005a10.229,10.229,0,0,0,1.488-.3,4.043,4.043,0,0,0,1.931-.864A20.277,20.277,0,0,1,79.261,19.765Z" transform="translate(-31.927 -6.75)" fill="#aaa"/></g><path d="M21.837,26.2c-7.205-2.487-8.539-9.153-6.742-13.82A8.381,8.381,0,0,1,27.526,8.662c4.361,3.127,3.9,9.126,3.9,9.126H20.247v-3.2h6.486a3.055,3.055,0,0,0-.9-2.436,3.932,3.932,0,0,0-4.845-.794c-1.257.666-2.871,3.435-1.923,7.408.9,3.017,3.726,4.215,6.27,4.214h0c3.789,0,6.658-2.638,6.663-6.958a16,16,0,1,0-3.809,10.342A10.225,10.225,0,0,1,21.837,26.2ZM7.678,18.013A1.825,1.825,0,1,1,9.5,16.188,1.825,1.825,0,0,1,7.678,18.013Z" transform="translate(0 0)" fill="#aaa"/></svg>',
                            width: 100,
                            margin: [0, 10, 0, 0]
                        },
                        {
                            width: '*',
                            text: '',
                        }
                    ]
                }
            );
        },

        rounding(plot, value) {
            if (plot === 'SKY_MOV') {
                return value.toFixed(3);
            } else {
                return value.toFixed(1);
            }
        },

        async downloadPdf() {
            SpinnerService.show();

            this.docDefinition.content = [];
            const app = document.querySelector('#app');
            const clone = app.cloneNode(true);
            document.body.appendChild(clone);
            clone.id = 'pdf-holder';

            if (!this.visualiseOnChart) {
                LongtermService.setLongtermVisualiseOnChart(true);
                this.visualiseOnChartFlag = true;
            }

            if (clone.querySelector('.with-chart')) {
                clone.querySelector('.with-chart').classList.remove('with-chart');
            }

            const startDate = UtilsService.momentUtcDate(TimeFrameService.getTimeFrame().start, true)
            const endDate = UtilsService.momentUtcDate(TimeFrameService.getTimeFrame().end, true)
            const observatory = ObservatoryService.getSelectedObservatory();
            const coordinates = observatory.latitude.toFixed(5) + ', ' + observatory.longitude.toFixed(5);
            const observatoryName = observatory.name + ' (' + observatory.code + ')';

            this.addLogos();
           
            this.addInformationLine(this.$t.object, ObjectsService.getDesignatorAndName(this.longtermObjectName));
            this.addInformationLine(this.$t.startDate, startDate);
            this.addInformationLine(this.$t.endDate, endDate);

            if (observatory.code) {
                this.addInformationLine(this.$t.observatory, observatoryName);
            }

            this.addInformationLine(this.$t.centreCoordinates, coordinates);
    
            this.addChartHeader('');

            const visuals = await html2canvas(document.querySelector('#pdf-holder .longterm-plots'), { scale: 1 });
            this.addImage(visuals.toDataURL('image/png'));
            document.body.removeChild(clone);

            for (const plot in this.object.plots) {
                if (this.object.plots[plot].enable) {
                    this.object.plots[plot].printable = true; /* eslint-disable-line */
                    this.object.plots[plot].enable = false; /* eslint-disable-line */
                }
            }
            
            for (const plot in this.object.plots) {
                if (this.object.plots[plot].printable) {
                    this.object.plots[plot].enable = true; /* eslint-disable-line */
                    LongtermService.setLongtermCurrentPlot(plot);
                    const units = this.object.plots[plot].units;
                    document.body.classList.add('pdf-generate');
                    await html2canvas(document.querySelector('.logo-img.pdf.esa'), { scale: 1 }); // do not remove
                    const chart = await html2canvas(document.querySelector('.longterm-plots__visuals'), { scale: 1 });
                    this.addLogos();
                    this.addChartHeader(`${this.$keys.titles[plot]} (${units})`);
                    this.addInformationLine(this.$t.selectedValue, this.rounding(plot, this.object.plots[plot].value) + ' ' + units);
                    this.addInformationLine(this.$t.minimumValue, this.rounding(plot, this.object.plots[plot].min) + ' ' + units);
                    this.addInformationLine(this.$t.maximumValue, this.rounding(plot, this.object.plots[plot].max) + ' ' + units);
                    this.addImage(chart.toDataURL('image/png'));
                    document.body.classList.remove('pdf-generate');
                    this.object.plots[plot].enable = false; /* eslint-disable-line */
                }
            }

            LongtermService.setLongtermCurrentPlot('');

            for (const plot in this.object.plots) {
                if (this.object.plots[plot].printable) {
                    delete this.object.plots[plot].printable; /* eslint-disable-line */
                    this.object.plots[plot].enable = true; /* eslint-disable-line */
                }
            }

            this.docDefinition.content[this.docDefinition.content.length - 1].pageBreak = false;

            pdfMake.vfs = pdfFonts.pdfMake.vfs;
            pdfMake.createPdf(this.docDefinition).download(this.fileName + '.pdf');

            if (this.visualiseOnChartFlag) {
                LongtermService.setLongtermVisualiseOnChart(false);
                this.visualiseOnChartFlag = false;
            }

            SpinnerService.hide();
        },
    },
}
</script>

<style>

</style>